import { Info } from 'lucide-react';
import { ReactNode } from 'react';
import { ArrayPath, FieldValues, useFieldArray, UseFormReturn } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';
import { Input } from '@/Domain/components/Shared/Input/Input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import AddIcon from '@/Domain/components/icons/AddIcon';
import { TrashIcon } from '@/Domain/components/icons/Trash.icon';
import { cn } from '@/Domain/utils/cn';

/**
 * The header labels and the input rows must share one track definition, or the
 * labels drift out of line with the fields they name. The third track is the
 * width of a row's delete button (IconButton size="sm" is w-[32px] = 2rem);
 * keep the two in step if that size ever changes.
 */
const COMPACT_GRID = 'grid grid-cols-[1fr_1fr_2rem] gap-2';

type Props<TForm extends FieldValues> = {
  form: UseFormReturn<TForm>;
  /** Field-array path, so a Remote MCP tool can host the same editor at allowed_tools.N.save_to_memory. */
  name: ArrayPath<TForm>;
  /** Slimmer chrome for the Remote MCP variant, where one editor sits inside each tool row. */
  compact?: boolean;
  /** Disambiguates test ids when several editors share a page. */
  testIdPrefix?: string;
  maxRows?: number;
  /** Intro copy, shown beside Add value above the compact header. */
  description?: ReactNode;
  /**
   * Which usage the key hint describes. An HTTP Action substitutes a saved value
   * server-side through a `{{metadata_<key>}}` placeholder you write yourself; an MCP
   * tool's inputs are the MCP server's own, so there is no placeholder and the value
   * only reaches the model as conversation data. Promising the placeholder in an MCP
   * sheet describes a mechanism that is not there.
   */
  hintVariant?: 'placeholder' | 'prompt';
};

/**
 * Editable list of response-value captures. Each row extracts a value from the
 * API response (JSONPath) and saves it to conversation memory under a key,
 * reusable in later steps as {{metadata_<key>}}.
 */
export const MemoryCapture = <TForm extends FieldValues>({
  form,
  name,
  compact = false,
  testIdPrefix = 'memory-capture',
  maxRows,
  description,
  hintVariant = 'placeholder',
}: Props<TForm>) => {
  const { t } = useTranslation('actions_page');

  const { fields, append, remove } = useFieldArray({ control: form.control, name });

  const handleAdd = () => append({ id: uuidv4(), json_path: '', metadata_key: '' } as never);

  const keyHint = (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* A real button, so the guidance is reachable by keyboard and not by hover alone. */}
          <button
            type="button"
            aria-label={t('sheet.form.memory_capture.key_hint_aria')}
            className="inline-flex cursor-help items-center rounded-full text-foreground-subtle transition-colors hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
            data-testid={`${testIdPrefix}-key-hint`}
          >
            <Info className="size-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          {hintVariant === 'prompt' ? (
            <span>{t('sheet.form.memory_capture.key_hint_prompt')}</span>
          ) : (
            <span>
              {t('sheet.form.memory_capture.key_hint')}{' '}
              <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
                {'{{metadata_<key>}}'}
              </code>
            </span>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <div>
      {compact ? (
        <>
          <div className="flex items-start justify-between gap-3 pb-2">
            <p className="text-xs text-muted-foreground">{description}</p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="-mt-1.5 shrink-0"
              onClick={handleAdd}
              disabled={maxRows !== undefined && fields.length >= maxRows}
              data-testid={`${testIdPrefix}-add-button`}
            >
              <AddIcon />
              {t('sheet.form.memory_capture.add_button')}
            </Button>
          </div>
          <div
            className={cn(
              COMPACT_GRID,
              'items-center pb-1.5 text-xs font-medium text-muted-foreground',
            )}
          >
            <span>{t('sheet.form.memory_capture.table.jsonpath')}</span>
            <span className="flex items-center gap-1.5">
              {t('sheet.form.memory_capture.table.memory_key')}
              {keyHint}
            </span>
          </div>
        </>
      ) : (
        <section className="relative grid h-16 grid-cols-[1fr_1fr_auto] items-center gap-2 border-b border-border bg-accent px-4 text-sm font-medium text-muted-foreground">
          <span>{t('sheet.form.memory_capture.table.jsonpath')}</span>
          <span className="flex items-center gap-1.5">
            {t('sheet.form.memory_capture.table.memory_key')}
            {keyHint}
          </span>
          <IconButton
            size="sm"
            onClick={handleAdd}
            disabled={maxRows !== undefined && fields.length >= maxRows}
            data-testid={`${testIdPrefix}-add-button`}
          >
            <AddIcon />
          </IconButton>
        </section>
      )}

      {fields.length === 0 && !compact ? (
        <div className="flex flex-col gap-3 items-center justify-center mt-7">
          <p className="text-sm font-medium text-foreground">
            {t('sheet.form.memory_capture.empty.title')}
          </p>
          <p className="text-center text-sm text-muted-foreground max-w-md">
            <Trans i18nKey="sheet.form.memory_capture.empty.description" t={t} />
          </p>
          <Button
            onClick={handleAdd}
            disabled={maxRows !== undefined && fields.length >= maxRows}
            data-testid={`${testIdPrefix}-add-empty-button`}
          >
            <AddIcon />
            {t('sheet.form.memory_capture.add_button')}
          </Button>
        </div>
      ) : (
        <div className={compact ? 'space-y-2' : 'mt-4 space-y-2'}>
          {fields.map((field, index) => (
            <div
              key={field.id}
              className={
                compact
                  ? cn(COMPACT_GRID, 'items-start')
                  : 'grid grid-cols-[1fr_1fr_auto] items-start gap-2 px-2'
              }
            >
              <FormField
                name={`${name}.${index}.json_path`}
                render={({ field: inputField, fieldState }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        data-testid={`${testIdPrefix}-jsonpath-${index}`}
                        aria-label={t('sheet.form.memory_capture.table.jsonpath')}
                        placeholder={t('sheet.form.memory_capture.jsonpath_placeholder')}
                        error={!!fieldState.error}
                        {...inputField}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name={`${name}.${index}.metadata_key`}
                render={({ field: inputField, fieldState }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        data-testid={`${testIdPrefix}-key-${index}`}
                        aria-label={t('sheet.form.memory_capture.table.memory_key')}
                        placeholder={t('sheet.form.memory_capture.key_placeholder')}
                        error={!!fieldState.error}
                        {...inputField}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <IconButton
                size="sm"
                variant="secondary"
                onClick={() => remove(index)}
                data-testid={`${testIdPrefix}-remove-${index}`}
                aria-label={t('sheet.form.memory_capture.remove_aria')}
              >
                <TrashIcon />
              </IconButton>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
